// ink-component
import React, { ReactNode, ReactNodeArray, MouseEvent } from 'react';
import { ThemeProvider } from 'styled-components';
import { LegacyVariants, FeedbackVariants } from '../_common/Types/GlobalTypes';

import Box from '../Box/Box';
import HStack from '../HStack/HStack';
import Icon from '../Icon/Icon';
import Li from '../Li/Li';
import Ornament from '../Ornament/Ornament';
import Text from '../Typography/Text/Text';
import Heading from '../Typography/Heading/Heading';
import Ul from '../Ul/Ul';
import VStack from '../VStack/VStack';

import useWindowWidth from '../../hooks/useWindowWidth';

import getGlobalProps from '../../helpers/constants/global-props';

import {
  StyledBanner,
  BannerContentWrapper,
  BannerContentBody,
  BannerContentFooter,
  BannerDismissAction,
  BannerPrimaryAction,
  BannerSecondaryAction,
  BannerHeaderWrapper,
} from './styles';
import { LinkPresetsType } from '../Anchor/Anchor';

/*
  Notes:

  1. The primary and secondary actions are both a normal `button` and `a` tag
  rather than dog-fooding Button and Anchor because this is its own variant.
*/

type PrimaryActionType = {
  'data-testid'?: string | number;
  href?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  text: string;
  preset?: LinkPresetsType;
};

type SecondaryActionType = {
  'data-testid'?: string | number;
  href?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  text: string;
  preset?: LinkPresetsType;
};

export interface BannerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** First line with bigger text for banner title */
  title?: ReactNode;
  /** Main line of text with the detailed information the banner has to provide */
  text?: ReactNode;
  /** Changes the color and the main icon of the banner  */
  type: Extract<LegacyVariants, 'error' | 'success' | 'info' | 'warning' | 'setup' | 'staff'>;
  /** Adds a button or link as a primary action  */
  primaryAction?: PrimaryActionType;
  /** Adds a secondary button with an extra action (needs primary action to work)  */
  secondaryAction?: SecondaryActionType;
  /** Determines the condition that shows the banner  */
  when?: any;
  /** Structures a series of information as bulleted items  */
  list?: ReactNodeArray;
  /** Callback for the dismiss icon  */
  onClose?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Removes built-in spacing */
  trim?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const globalProps = getGlobalProps({});

const ornamentVariant: {
  [key in BannerProps['type']]: Extract<
    FeedbackVariants,
    'feedback-informational' | 'feedback-negative' | 'feedback-notice' | 'feedback-positive' | 'feedback-staff'
  >;
} = {
  error: 'feedback-negative',
  info: 'feedback-informational',
  setup: 'feedback-notice',
  staff: 'feedback-staff',
  success: 'feedback-positive',
  warning: 'feedback-notice',
} as const;

/**
 * @purpose Highlights important information with possible immediate actions
 * @categories accessory
 * @aka dialog, flag
 * @HTMLType HTMLDivElement
 *
 */

function Banner(props: BannerProps) {
  const {
    className,
    list,
    onClose,
    primaryAction,
    secondaryAction,
    style,
    text,
    title,
    trim,
    type,
    when,
    ...otherProps
  } = props;

  const { isMobile } = useWindowWidth();

  if (!when) return null;

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        bannerType: type,
        bannerMobile: isMobile,
      })}
    >
      <StyledBanner {...globalProps} {...otherProps}>
        <BannerContentWrapper>
          <BannerHeaderWrapper>
            <Ornament size={isMobile ? 'small' : 'medium'} variant={ornamentVariant[type]} />

            {isMobile && onClose && (
              <BannerDismissAction onClick={onClose}>
                <Icon type="close" />
              </BannerDismissAction>
            )}
          </BannerHeaderWrapper>

          <VStack spacing={!text && !list ? '0' : '4'}>
            {title && (
              <>
                {isMobile ? (
                  <Text variant="body-2" trim as="h4" data-testid="banner-title">
                    {title}
                  </Text>
                ) : (
                  <Heading variant="heading-4" trim data-testid="banner-title">
                    {title}
                  </Heading>
                )}
              </>
            )}

            {(text || list) && (
              <BannerContentBody>
                <VStack spacing="xsmall">
                  <Text trim>{text}</Text>
                  {list && list.length > 0 && (
                    <Ul>
                      {list.map((item, key) => (
                        <Li key={key}>{item}</Li>
                      ))}
                    </Ul>
                  )}
                </VStack>
              </BannerContentBody>
            )}

            {primaryAction && (
              <BannerContentFooter>
                <HStack spacing={isMobile ? 'none' : 'medium'}>
                  <BannerPrimaryAction
                    {...primaryAction}
                    as={primaryAction.href ? 'a' : 'button'}
                    theme={{ buttonPreset: primaryAction.preset, buttonSize: isMobile ? 'small' : 'medium' }}
                  >
                    {primaryAction.text}
                  </BannerPrimaryAction>

                  {secondaryAction && (
                    <BannerSecondaryAction
                      {...secondaryAction}
                      as={secondaryAction.href ? 'a' : 'button'}
                      theme={{ buttonPreset: secondaryAction.preset, buttonSize: isMobile ? 'small' : 'medium' }}
                    >
                      {secondaryAction.text}
                    </BannerSecondaryAction>
                  )}
                </HStack>
              </BannerContentFooter>
            )}
          </VStack>
        </BannerContentWrapper>

        {!isMobile && onClose && (
          <BannerDismissAction onClick={onClose}>
            <Icon type="close" />
          </BannerDismissAction>
        )}
      </StyledBanner>

      {/* Originally Snackbar had `margin-bottom: 19px` so if the Box becomes an issue (uses padding), we can revert! */}
      {!trim && <Box bottom="large" />}
    </ThemeProvider>
  );
}

Banner.defaultProps = {
  title: undefined,
  text: undefined,
  type: 'info',
  primaryAction: undefined,
  secondaryAction: undefined,
  list: undefined,
  onClose: undefined,
  when: true,
  trim: false,
  'data-testid': undefined,
};

Banner.displayName = 'Banner';

export default Banner;
